import { useCallback, useEffect, useRef, useState } from 'react';
import type { ValidationIssueNavigationContext } from '../../../shared/types/validationNavigation.types';
import type { ValidationWorkspaceTab } from '../../../shared/stores/usePcfValidationStore';
import {
  useWorkflowResumeStore,
  type MappingWorkspaceResume,
} from '../../../shared/stores/useWorkflowResumeStore';
import type { MappingSelectedNode } from '../lib/mappingWorkspacePageModel';
import {
  resolveInitialMappingWorkspaceTabKey,
  resolveRequestedWorkspaceTab,
  toMappingWorkspaceTabKey,
  toMappingWorkspaceTabIndex,
  type MappingWorkspaceTabIndex,
} from '../lib/mappingWorkspacePageModel';
import { isMappingTourTreeFocusStep } from '../lib/mappingWorkspaceGuideContent';
import {
  hasSeenMappingWorkspaceOnboarding,
  markMappingWorkspaceOnboardingSeen,
} from '../lib/mappingWorkspaceOnboarding';
import { traceMappingFlow } from '../lib/mappingTrace';

const shouldLogSelection = false;
const MobileMappingMenuTourStepId = 'mobile-mapping-menu';

interface ObjectTypeOption {
  name: string;
}

interface UseMappingWorkspaceOrchestrationArgs {
  showMappingWorkspaceSummary: boolean;
  objectTypeOptions: ObjectTypeOption[];
  onSelectObjectType: (name: string) => void;
  requestedWorkspaceTab: ValidationWorkspaceTab | null;
  issueNavigationContext: ValidationIssueNavigationContext | null;
  openQuickFillGuide?: () => void;
  openQuickFillTour?: () => void;
  openValidationGuide?: () => void;
  openValidationTour?: () => void;
  openExportGuide?: () => void;
  openExportTour?: () => void;
  mappingTourSteps?: Array<{
    id: string;
    targetNode?: MappingSelectedNode;
  }>;
  initialSelectedNode?: MappingSelectedNode;
  initialSearch?: string;
  resumeScopeKey?: string | null;
  restorePersistedWorkspace?: boolean;
}

function resolveMappingIssueTargetNode(
  issueNavigationContext: ValidationIssueNavigationContext | null,
  objectTypeOptions: ObjectTypeOption[]
): MappingSelectedNode | null {
  if (issueNavigationContext?.targetPage !== 'mapping') {
    return null;
  }

  const requestedFocus = issueNavigationContext.actionParams?.mappingFocus;
  if (requestedFocus?.nodeType && requestedFocus.nodeType !== 'objectType') {
    return { type: requestedFocus.nodeType };
  }

  const fallbackObjectType =
    objectTypeOptions.find((objectType) => (objectType.name || '').trim().toUpperCase() === 'ALL')
      ?.name || objectTypeOptions[0]?.name;

  if (!fallbackObjectType) {
    return null;
  }

  if (requestedFocus?.nodeType === 'objectType') {
    const requestedObjectTypeName = (requestedFocus.nodeName || '').trim();
    const matchedObjectType =
      objectTypeOptions.find(
        (objectType) =>
          (objectType.name || '').trim().toUpperCase() === requestedObjectTypeName.toUpperCase()
      )?.name || fallbackObjectType;

    return {
      type: 'objectType',
      name: matchedObjectType,
    };
  }

  return {
    type: 'objectType',
    name: fallbackObjectType,
  };
}

function shouldRestoreSummaryWindow(input: {
  persistedResume: MappingWorkspaceResume | undefined;
  showMappingWorkspaceSummary: boolean;
  nextTab: MappingWorkspaceTabIndex;
  requestedWorkspaceTabIndex: MappingWorkspaceTabIndex | null;
  requestedTabKey: string | null;
  onboardingSeen: boolean;
}): boolean {
  return (
    // The summary is the onboarding surface: auto-open only on the first ever
    // workspace open; afterwards it is reachable manually (ribbon, buttons).
    !input.onboardingSeen &&
    Boolean(input.persistedResume) &&
    input.showMappingWorkspaceSummary &&
    input.nextTab === 0 &&
    input.requestedWorkspaceTabIndex === null &&
    !input.requestedTabKey &&
    input.persistedResume?.landingMode === 'summary'
  );
}

export const useMappingWorkspaceOrchestration = ({
  showMappingWorkspaceSummary,
  objectTypeOptions,
  onSelectObjectType,
  requestedWorkspaceTab,
  issueNavigationContext,
  openQuickFillGuide,
  openQuickFillTour,
  openValidationGuide,
  openValidationTour,
  openExportGuide,
  openExportTour,
  mappingTourSteps = [],
  initialSelectedNode = { type: 'objectType', name: 'ALL' },
  initialSearch,
  resumeScopeKey,
  restorePersistedWorkspace = true,
}: UseMappingWorkspaceOrchestrationArgs) => {
  const persistedResume = useWorkflowResumeStore((state) =>
    resumeScopeKey ? state.mappingByScope[resumeScopeKey] : undefined
  );
  const effectivePersistedResume = restorePersistedWorkspace ? persistedResume : undefined;
  const setMappingResume = useWorkflowResumeStore((state) => state.setMappingResume);
  const [selectedNode, setSelectedNode] = useState<MappingSelectedNode>(
    effectivePersistedResume?.selectedNode ?? initialSelectedNode
  );
  const [isTreeCollapsed, setIsTreeCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTabState, setActiveTabState] = useState<MappingWorkspaceTabIndex>(() => {
    const requestedWorkspaceTabIndex = resolveRequestedWorkspaceTab(requestedWorkspaceTab);
    const requestedTabKey = resolveInitialMappingWorkspaceTabKey(
      initialSearch ?? window.location.search
    );

    if (requestedWorkspaceTabIndex !== null) {
      return requestedWorkspaceTabIndex;
    }

    if (requestedTabKey) {
      return toMappingWorkspaceTabIndex(resolveRequestedWorkspaceTab(requestedTabKey) ?? 0);
    }

    return toMappingWorkspaceTabIndex(
      resolveRequestedWorkspaceTab(effectivePersistedResume?.activeTab ?? 'mapping') ?? 0
    );
  });
  const [summaryWindowOpen, setSummaryWindowOpen] = useState(() => {
    const requestedWorkspaceTabIndex = resolveRequestedWorkspaceTab(requestedWorkspaceTab);
    const requestedTabKey = resolveInitialMappingWorkspaceTabKey(
      initialSearch ?? window.location.search
    );
    const initialTab = toMappingWorkspaceTabIndex(
      requestedWorkspaceTabIndex ??
        resolveRequestedWorkspaceTab(
          requestedTabKey ?? effectivePersistedResume?.activeTab ?? 'mapping'
        ) ??
        0
    );

    return shouldRestoreSummaryWindow({
      persistedResume: effectivePersistedResume,
      showMappingWorkspaceSummary,
      nextTab: initialTab,
      requestedWorkspaceTabIndex,
      requestedTabKey,
      onboardingSeen: hasSeenMappingWorkspaceOnboarding(),
    });
  });
  const [mappingGuideOpen, setMappingGuideOpen] = useState(false);
  const [mappingTourOpen, setMappingTourOpen] = useState(false);
  const [mappingTourStep, setMappingTourStep] = useState(0);
  const activeTab = activeTabState;
  const mappingTourStartNodeRef = useRef<MappingSelectedNode>(
    effectivePersistedResume?.selectedNode ?? initialSelectedNode
  );
  const initializedScopeRef = useRef<string | null>(null);
  const activeTabRef = useRef(activeTabState);
  const explicitWorkspaceTabRef = useRef(
    resolveRequestedWorkspaceTab(requestedWorkspaceTab) !== null ||
      Boolean(resolveInitialMappingWorkspaceTabKey(initialSearch ?? window.location.search))
  );

  useEffect(() => {
    activeTabRef.current = activeTabState;
  }, [activeTabState]);

  const persistResume = useCallback(
    (
      updater: Parameters<typeof setMappingResume>[1]
    ) => {
      if (!resumeScopeKey) {
        return;
      }

      setMappingResume(resumeScopeKey, updater);
    },
    [resumeScopeKey, setMappingResume]
  );

  const initializeFromResume = useCallback(() => {
    const requestedWorkspaceTabIndex = resolveRequestedWorkspaceTab(requestedWorkspaceTab);
    const requestedTabKey = resolveInitialMappingWorkspaceTabKey(
      initialSearch ?? window.location.search
    );
    const nextTab = toMappingWorkspaceTabIndex(
      requestedWorkspaceTabIndex ??
        resolveRequestedWorkspaceTab(
          requestedTabKey ?? effectivePersistedResume?.activeTab ?? 'mapping'
        ) ??
        0
    );
    const nextNode = effectivePersistedResume?.selectedNode ?? initialSelectedNode;
    const shouldOpenSummary = shouldRestoreSummaryWindow({
      persistedResume: effectivePersistedResume,
      showMappingWorkspaceSummary,
      nextTab,
      requestedWorkspaceTabIndex,
      requestedTabKey,
      onboardingSeen: hasSeenMappingWorkspaceOnboarding(),
    });
    if (shouldOpenSummary) {
      // Persist immediately: even if the user never dismisses the dialog
      // properly, later page opens (and other profile scopes) stay clean.
      markMappingWorkspaceOnboardingSeen();
    }

    traceMappingFlow('workspace resume init', {
      resumeScopeKey,
      hasPersistedResume: Boolean(effectivePersistedResume),
      restorePersistedWorkspace,
      storedResumeSuppressed: Boolean(persistedResume) && !restorePersistedWorkspace,
      persistedActiveTab: persistedResume?.activeTab ?? null,
      persistedLandingMode: persistedResume?.landingMode ?? null,
      persistedSelectedNode: persistedResume?.selectedNode ?? null,
      requestedWorkspaceTab,
      requestedTabKey,
      nextTab,
      nextTabKey: toMappingWorkspaceTabKey(nextTab),
      nextNode,
      shouldOpenSummary,
    });

    activeTabRef.current = nextTab;
    setActiveTabState(nextTab);
    setSelectedNode(nextNode);
    setSummaryWindowOpen(shouldOpenSummary);

    // An explicit tab — asked for by the caller or carried in the URL — is the
    // tab the user actually lands on, so it is the tab a later reopen must
    // resume. Leaving the stored tab untouched would rewind the workspace to a
    // tab the user has already navigated away from.
    const explicitTabKey =
      requestedWorkspaceTabIndex !== null
        ? toMappingWorkspaceTabKey(requestedWorkspaceTabIndex)
        : requestedTabKey;

    if (resumeScopeKey && explicitTabKey) {
      traceMappingFlow('workspace resume write explicit tab override', {
        resumeScopeKey,
        explicitTabKey,
        source: requestedWorkspaceTabIndex !== null ? 'requestedWorkspaceTab' : 'url',
      });
      setMappingResume(resumeScopeKey, (current) => ({
        ...current,
        activeTab: explicitTabKey,
        landingMode: 'workspace',
      }));
      return;
    }

    if (resumeScopeKey && !effectivePersistedResume) {
      traceMappingFlow('workspace resume seed new scope', {
        resumeScopeKey,
        activeTab: toMappingWorkspaceTabKey(nextTab),
        selectedNode: nextNode,
        landingMode: shouldOpenSummary ? 'summary' : 'workspace',
      });
      setMappingResume(resumeScopeKey, (current) => ({
        ...current,
        activeTab: toMappingWorkspaceTabKey(nextTab),
        selectedNode: nextNode,
        landingMode: shouldOpenSummary ? 'summary' : 'workspace',
      }));
    }
  }, [
    initialSearch,
    initialSelectedNode,
    effectivePersistedResume,
    persistedResume,
    requestedWorkspaceTab,
    restorePersistedWorkspace,
    resumeScopeKey,
    setMappingResume,
    showMappingWorkspaceSummary,
  ]);

  useEffect(() => {
    const nextScopeKey = resumeScopeKey ?? '__default__';
    if (initializedScopeRef.current === nextScopeKey) {
      return;
    }

    initializedScopeRef.current = nextScopeKey;
    initializeFromResume();
  }, [initializeFromResume, resumeScopeKey]);

  useEffect(() => {
    if (restorePersistedWorkspace || !resumeScopeKey || !persistedResume) {
      return;
    }

    traceMappingFlow('workspace persisted tab suppressed', {
      resumeScopeKey,
      persistedActiveTab: persistedResume.activeTab ?? null,
      persistedLandingMode: persistedResume.landingMode ?? null,
    });

    if (explicitWorkspaceTabRef.current) {
      traceMappingFlow('workspace persisted tab suppression skipped for explicit tab', {
        resumeScopeKey,
        activeTab: activeTabRef.current,
        activeTabKey: toMappingWorkspaceTabKey(activeTabRef.current),
      });
      return;
    }

    if (activeTabRef.current !== 0) {
      activeTabRef.current = 0;
      setActiveTabState(0);
      setSummaryWindowOpen(false);
    }

    if (
      persistedResume.activeTab !== 'mapping' ||
      persistedResume.landingMode !== 'workspace'
    ) {
      setMappingResume(resumeScopeKey, (current) => ({
        ...current,
        activeTab: 'mapping',
        landingMode: 'workspace',
      }));
    }
  }, [
    persistedResume,
    restorePersistedWorkspace,
    resumeScopeKey,
    setMappingResume,
  ]);

  const setActiveTab = useCallback(
    (nextTab: MappingWorkspaceTabIndex) => {
      const previousTab = activeTabRef.current;
      explicitWorkspaceTabRef.current = true;
      activeTabRef.current = nextTab;
      traceMappingFlow('workspace tab set', {
        resumeScopeKey,
        previousTab,
        nextTab,
        nextTabKey: toMappingWorkspaceTabKey(nextTab),
      });
      setActiveTabState(nextTab);
      setSummaryWindowOpen(false);

      if (nextTab !== 0) {
        setMappingGuideOpen(false);
        setMappingTourOpen(false);
        setMappingTourStep(0);
        setMobileMenuOpen(false);
        setSelectedNode((currentNode) =>
          mappingTourOpen ? mappingTourStartNodeRef.current : currentNode
        );
      }

      persistResume((current) => ({
        ...current,
        activeTab: toMappingWorkspaceTabKey(nextTab),
        landingMode: 'workspace',
      }));
    },
    [mappingTourOpen, persistResume, resumeScopeKey]
  );

  const resolveComponentsNodeTarget = useCallback((): string | null => {
    const fallbackObjectType =
      objectTypeOptions.find((objectType) => (objectType.name || '').trim().toUpperCase() === 'ALL')
        ?.name || objectTypeOptions[0]?.name;

    return fallbackObjectType ?? null;
  }, [objectTypeOptions]);

  const handleNodeSelect = useCallback(
    (type: string, name?: string, options?: { persist?: boolean }) => {
      if (shouldLogSelection) {
        console.info('[MappingWorkspaceOrchestration] handleNodeSelect requested', {
          nextType: type,
          nextName: name || null,
        });
      }

      const resolvedType = type === 'components' ? 'objectType' : type;
      const resolvedName = type === 'components' ? resolveComponentsNodeTarget() : name;

      setSelectedNode((previousNode) => {
        if (shouldLogSelection) {
          console.info('[MappingWorkspaceOrchestration] selectedNode transition', {
            previousType: previousNode.type,
            previousName: previousNode.name || null,
            nextType: resolvedType,
            nextName: resolvedName || null,
          });
        }
        // Persisted selected node schema uses `undefined` for "no name" so it
        // round-trips cleanly through JSON; `null` would serialize and revive as null.
        return { type: resolvedType, name: resolvedName ?? undefined };
      });

      if (options?.persist !== false) {
        traceMappingFlow('workspace node persisted', {
          resumeScopeKey,
          type: resolvedType,
          name: resolvedName ?? null,
        });
        persistResume((current) => ({
          ...current,
          // Keep persistence schema consistent with the in-memory node shape.
          selectedNode: { type: resolvedType, name: resolvedName ?? undefined },
        }));
      }

      if (resolvedType === 'objectType' && resolvedName) {
        onSelectObjectType(resolvedName);
      }
    },
    [onSelectObjectType, persistResume, resumeScopeKey, resolveComponentsNodeTarget]
  );

  useEffect(() => {
    const nextTargetNode = resolveMappingIssueTargetNode(issueNavigationContext, objectTypeOptions);
    if (!nextTargetNode) {
      return;
    }

    if (
      selectedNode.type === nextTargetNode.type &&
      selectedNode.name === nextTargetNode.name
    ) {
      return;
    }

    if (shouldLogSelection) {
      console.info('[MappingWorkspaceOrchestration] Issue navigation forcing mapping selection', {
        targetPage: issueNavigationContext?.targetPage,
        nextTargetNode,
        familyId: issueNavigationContext?.familyId || null,
      });
    }
    handleNodeSelect(nextTargetNode.type, nextTargetNode.name);
  }, [
    handleNodeSelect,
    issueNavigationContext,
    issueNavigationContext?.actionParams?.mappingFocus,
    issueNavigationContext?.familyId,
    issueNavigationContext?.targetPage,
    objectTypeOptions,
    selectedNode.name,
    selectedNode.type,
  ]);

  useEffect(() => {
    if (selectedNode.type !== 'objectType') {
      return;
    }

    const hasMatchingObjectType = objectTypeOptions.some(
      (objectType) => objectType.name === selectedNode.name
    );

    if (hasMatchingObjectType) {
      return;
    }

    const fallbackObjectType =
      objectTypeOptions.find((objectType) => (objectType.name || '').trim().toUpperCase() === 'ALL')
        ?.name || objectTypeOptions[0]?.name;

    if (!fallbackObjectType) {
      return;
    }

    handleNodeSelect('objectType', fallbackObjectType);
  }, [handleNodeSelect, objectTypeOptions, selectedNode.name, selectedNode.type]);

  useEffect(() => {
    if (!showMappingWorkspaceSummary) {
      setSummaryWindowOpen(false);
    }
  }, [showMappingWorkspaceSummary]);

  useEffect(() => {
    const currentTourTargetNode = mappingTourSteps[mappingTourStep]?.targetNode;
    if (!mappingTourOpen || activeTab !== 0 || !currentTourTargetNode) {
      return;
    }

    if (
      selectedNode.type === currentTourTargetNode.type &&
      selectedNode.name === currentTourTargetNode.name
    ) {
      return;
    }

    if (shouldLogSelection) {
      console.info('[MappingWorkspaceOrchestration] Tour forcing selection', {
        step: mappingTourStep,
        targetType: currentTourTargetNode.type,
        targetName: currentTourTargetNode.name || null,
      });
    }
    handleNodeSelect(currentTourTargetNode.type, currentTourTargetNode.name, {
      persist: false,
    });
  }, [activeTab, handleNodeSelect, mappingTourOpen, mappingTourStep, mappingTourSteps, selectedNode.name, selectedNode.type]);

  useEffect(() => {
    if (!mappingTourOpen || activeTab !== 0) {
      return;
    }

    const stepId = mappingTourSteps[mappingTourStep]?.id;
    if (!isMappingTourTreeFocusStep(stepId)) {
      return;
    }

    setIsTreeCollapsed(false);
  }, [activeTab, mappingTourOpen, mappingTourStep, mappingTourSteps, setIsTreeCollapsed]);

  useEffect(() => {
    if (!mappingTourOpen || activeTab !== 0) {
      return;
    }

    const isMobileTour = mappingTourSteps.some(
      (step) => step.id === MobileMappingMenuTourStepId
    );
    if (!isMobileTour) {
      return;
    }

    const stepId = mappingTourSteps[mappingTourStep]?.id;
    setMobileMenuOpen(
      stepId !== MobileMappingMenuTourStepId && isMappingTourTreeFocusStep(stepId)
    );
  }, [activeTab, mappingTourOpen, mappingTourStep, mappingTourSteps]);

  const handleCloseMappingTour = useCallback(() => {
    const wasOpen = mappingTourOpen;
    setMappingTourOpen(false);
    setMappingTourStep(0);
    setMobileMenuOpen(false);
    if (wasOpen) {
      handleNodeSelect(mappingTourStartNodeRef.current.type, mappingTourStartNodeRef.current.name, {
        persist: false,
      });
    }
  }, [handleNodeSelect, mappingTourOpen]);

  const handleOpenMappingGuide = useCallback(() => {
    handleCloseMappingTour();
    setSummaryWindowOpen(false);
    persistResume((current) => ({
      ...current,
      landingMode: 'workspace',
    }));
    setMappingGuideOpen(true);
  }, [handleCloseMappingTour, persistResume]);

  const handleOpenMappingTour = useCallback(() => {
    mappingTourStartNodeRef.current = selectedNode;
    setSummaryWindowOpen(false);
    setMappingGuideOpen(false);
    setMobileMenuOpen(false);
    setMappingTourStep(0);
    setMappingTourOpen(true);
    persistResume((current) => ({
      ...current,
      landingMode: 'workspace',
    }));
  }, [persistResume, selectedNode]);

  const handleCloseSummaryWindow = useCallback(() => {
    setSummaryWindowOpen(false);
    persistResume((current) => ({
      ...current,
      landingMode: 'workspace',
    }));
  }, [persistResume]);

  const handleOpenSummaryWindow = useCallback(() => {
    setMappingGuideOpen(false);
    handleCloseMappingTour();
    setSummaryWindowOpen(true);
  }, [handleCloseMappingTour]);

  /**
   * Closes every workspace overlay and remembers that the user last stood in
   * the workspace itself. Navigation is deliberately not part of this: the
   * caller runs it through the navigation guard.
   */
  const closeWorkspaceOverlays = useCallback(() => {
    setSummaryWindowOpen(false);
    setMappingGuideOpen(false);
    handleCloseMappingTour();
    persistResume((current) => ({
      ...current,
      landingMode: 'workspace',
    }));
  }, [handleCloseMappingTour, persistResume]);

  const handleOpenActivePageGuide = useCallback(() => {
    if (activeTab === 0) {
      handleOpenMappingGuide();
      return;
    }

    if (activeTab === 1) {
      openQuickFillGuide?.();
      return;
    }

    if (activeTab === 2) {
      openValidationGuide?.();
      return;
    }

    if (activeTab === 3) {
      openExportGuide?.();
    }
  }, [activeTab, handleOpenMappingGuide, openExportGuide, openQuickFillGuide, openValidationGuide]);

  const handleOpenActivePageTour = useCallback(() => {
    if (activeTab === 0) {
      handleOpenMappingTour();
      return;
    }

    if (activeTab === 1) {
      openQuickFillTour?.();
      return;
    }

    if (activeTab === 2) {
      openValidationTour?.();
      return;
    }

    if (activeTab === 3) {
      openExportTour?.();
    }
  }, [activeTab, handleOpenMappingTour, openExportTour, openQuickFillTour, openValidationTour]);

  return {
    activeTab,
    setActiveTab,
    selectedNode,
    handleNodeSelect,
    isTreeCollapsed,
    setIsTreeCollapsed,
    mobileMenuOpen,
    setMobileMenuOpen,
    summaryWindowOpen,
    mappingGuideOpen,
    setMappingGuideOpen,
    mappingTourOpen,
    mappingTourStep,
    setMappingTourStep,
    handleCloseMappingTour,
    handleOpenMappingGuide,
    handleOpenMappingTour,
    handleCloseSummaryWindow,
    handleOpenSummaryWindow,
    closeWorkspaceOverlays,
    handleOpenActivePageGuide,
    handleOpenActivePageTour,
  };
};
